import { PeriodRangePicker } from '@app/components/period-range-picker/period-range-picker';
import { InsightsFilterOption, InsightsTimePeriod } from '@shared/api/types';
import { MultiSelect } from '@shared/components/multi-select';
import { useMemo } from 'react';
import { useItemScope } from './use-item-scope';

type Props = {
  /** BE-echoed default window boundaries, shown when no explicit selection. */
  defaultStart?: InsightsTimePeriod | null;
  defaultEnd?: InsightsTimePeriod | null;
  /** Sites the item has purchases for; the filter hides itself without any. */
  siteOptions?: readonly InsightsFilterOption[];
};

/**
 * The item detail page's persistent scope controls: a site filter and the
 * time-period range picker. State lives in the URL and carries across tabs.
 */
export function ItemScopeToolbar({
  defaultStart,
  defaultEnd,
  siteOptions,
}: Props) {
  const { periodStart, periodEnd, sites, setPeriodRange, setSites } =
    useItemScope();

  const options = useMemo(
    () =>
      (siteOptions ?? [])
        .map((site) => ({ label: site.name, value: site.uuid }))
        .sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
        ),
    [siteOptions]
  );

  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {options.length > 0 || sites.length > 0 ? (
        <MultiSelect
          options={options}
          defaultValue={sites}
          onValueChange={setSites}
          placeholder="All sites"
          itemName="site"
          className="bg-gray-00"
          autoSize
          hideSelectAll
          responsive={{
            mobile: { maxCount: 1 },
            tablet: { maxCount: 1 },
            desktop: { maxCount: 1 },
          }}
          maxWidth="24rem"
        />
      ) : null}
      <PeriodRangePicker
        start={periodStart}
        end={periodEnd}
        onChange={setPeriodRange}
        defaultStart={defaultStart}
        defaultEnd={defaultEnd}
      />
    </div>
  );
}
